import { Combobox } from '@headlessui/react';
import styled, { css } from 'styled-components';

import { Box } from '@/shared/ui/box';
import { Caption } from '@/shared/ui/caption';
import { Icon } from '@/shared/ui/icon';

import { AutocompleteProps, StyledButtonDisabled, StyledOptional, StyledSearchInOptions } from './autocomplete.types';

const SEARCH_WRAPPER_PADDING = 10;
const POSITION = SEARCH_WRAPPER_PADDING / 2;

export const optionsCss = css`
	padding: 8px 12px 8px 36px;
	outline: none;
	color: ${({ theme: { option } }) => option.color};
	background-color: ${({ theme: { option } }) => option.background};
	font-weight: 500;
	cursor: pointer;
	transition: ${({ theme: { transition } }) => transition.primary};

	&:hover {
		background-color: ${({ theme: { option } }) => option.hover};
	}
`;

export const Input = styled(Combobox.Input)<
	Required<Pick<AutocompleteProps<string>, 'variant' | 'paragraph'>> & StyledOptional & { hasValue?: boolean }
>`
	outline: none;
	font-size: 15px;
	padding: ${({ adornment }) => (adornment ? '8px 16px 8px 42px' : '8px 16px')};
	border: 1px solid ${({ variant, theme: { input } }) => input[variant].border};
	background-color: ${({ variant, theme: { input } }) => input[variant].background};
	color: ${({ variant, theme: { input } }) => input[variant].filled.color};
	-webkit-text-fill-color: ${({ variant, theme: { input } }) => input[variant].filled.color};
	border-radius: ${({ variant, theme: { input } }) => input[variant].radius};
	margin-bottom: ${({ paragraph }) => paragraph};
	width: 100%;

	&:focus {
		border: 1px solid ${({ variant, theme: { input } }) => input[variant].focus.border};
		color: ${({ variant, theme: { input } }) => input[variant].focus.color};
		-webkit-text-fill-color: ${({ variant, theme: { input } }) => input[variant].focus.color};
	}
`;

export const Options = styled(Combobox.Options)<Required<Pick<AutocompleteProps<string>, 'variant'>> & StyledOptional>`
	outline: none;
	width: 100%;
	position: absolute;
	top: 100%;
	margin-top: 8px;
	overflow-y: ${({ scroll }) => (scroll ? 'scroll' : 'hidden')};
	min-height: ${({ scroll }) => (scroll ? '230px' : 'hidden')};
	max-height: ${({ scroll }) => (scroll ? '230px' : 'hidden')};
	overflow-x: hidden;
	z-index: ${({ theme: { zIndex } }) => zIndex.menu};
	border-radius: ${({ variant, theme: { input } }) => input[variant].radius};
	background-color: ${({ theme: { option } }) => option.background};
	border: ${({ border, theme: { option } }) => (border ? option.border : 'none')};
`;

export const Option = styled(Combobox.Option)`
	${optionsCss}
`;

export const NoMatchesOption = styled.li`
	${optionsCss}
`;

export const Adornment = styled(Icon)`
	position: absolute;
	left: 17px;
	top: 25%;
`;

export const Selected = styled.div<{ selected?: boolean }>`
	position: relative;
	display: flex;
	align-items: center;
	color: ${({ selected, theme: { option } }) => (selected ? option.selectedColor : option.color)};
`;

export const Label = styled(Caption)`
	margin-bottom: 8px;

	& sup {
		color: ${({
			theme: {
				colors: { error },
			},
		}) => error};
	}
`;

export const SearchInput = styled(Input)`
	padding: 8px 16px 8px 42px;
	background-color: ${({ theme: { option } }) => option.background};

	&:focus {
		border: 1px solid ${({ variant, theme: { input } }) => input[variant].border};
	}
`;

export const SearchIcon = styled(Icon)`
	position: absolute;
	z-index: 5;
	left: ${`calc(17px + ${POSITION}px)`};
	top: ${`calc(25% + ${POSITION}px)`};
`;

export const SearchWrapper = styled(Box)`
	position: sticky;
	top: 0;
	background-color: ${({ theme: { option } }) => option.background};
	padding: ${`${SEARCH_WRAPPER_PADDING}px`};
	z-index: 6;
`;

export const Button = styled(Combobox.Button)<
	Required<Pick<AutocompleteProps<string>, 'variant'>> &
		StyledOptional &
		StyledSearchInOptions &
		StyledButtonDisabled & { hasValue?: boolean }
>`
	display: flex;
	align-items: center;
	width: 100%;
	border: none;
	outline: none;
	background: none;
	color: ${({ variant, open, hasValue, theme: { input } }) =>
		open ? input[variant].focus.color : hasValue ? input[variant].filled?.color : input[variant].fontColor};
	white-space: pre-wrap;
	padding-right: 24px;
	min-height: 37px;
	height: 100%;

	&[aria-expanded='true'] {
		border: 1px solid ${({ variant, theme: { input } }) => input[variant].focus.border};
		color: ${({ variant, theme: { input } }) => input[variant].focus.color};
	}

	${({ searchinoptions, variant, open }) =>
		searchinoptions &&
		css`
			padding: 8px 16px;
			text-align: left;
			cursor: pointer;
			border: 1px solid ${({ theme: { input } }) => (open ? input[variant].focus.border : input[variant].border)};
			background-color: ${({ theme: { input } }) => input[variant].background};
			font-size: 15px;
			border-radius: ${({ theme: { input } }) => input[variant].radius};
			margin-right: 24px;
		`}

	${({ isDisabled }) =>
		isDisabled &&
		css`
			cursor: not-allowed;
			opacity: 0.6;
		`}
`;

export const CheckIconWrapper = styled.span`
	position: absolute;
	left: -22px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
`;

export const AutocompleteArrow = styled(Box)<{ open: boolean; disabled?: boolean }>`
	right: 18px;
	transform: rotate(${({ open }) => (open ? '180deg' : 0)});
	transition: transform 0.2s ease;

	& svg {
		fill: ${({ theme: { select }, disabled }) => {
			if (disabled) return select.fill.disabled;

			return select.fill.default;
		}};
	}
`;
